import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme,
} from "@mui/material";
import DevelopmentBadge from "../../../components/DevelopmentBadge";
import {
  People,
  History,
  Feedback,
  Category,
  Assessment,
  ArrowForward,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const ClientContent = () => {
  const theme = useTheme();
  const isDevelopment = process.env.REACT_APP_ISDEVELOPMENT === "true";

  const { t } = useTranslation();

  const handleLogin = () => {
    window.open(`https://client.joglo.ink`, "_blank");
  };
  const features = [
    {
      icon: <People fontSize="large" />,
      title: t("Dashboard_clientcontent.features.clientDatabase.title"),
      description: t(
        "Dashboard_clientcontent.features.clientDatabase.description"
      ),
    },
    {
      icon: <History fontSize="large" />,
      title: t("Dashboard_clientcontent.features.interactionTracking.title"),
      description: t(
        "Dashboard_clientcontent.features.interactionTracking.description"
      ),
    },
    {
      icon: <Feedback fontSize="large" />,
      title: t("Dashboard_clientcontent.features.feedbackManagement.title"),
      description: t(
        "Dashboard_clientcontent.features.feedbackManagement.description"
      ),
    },
    {
      icon: <Category fontSize="large" />,
      title: t("Dashboard_clientcontent.features.clientSegmentation.title"),
      description: t(
        "Dashboard_clientcontent.features.clientSegmentation.description"
      ),
    },
    {
      icon: <Assessment fontSize="large" />,
      title: t("Dashboard_clientcontent.features.reporting.title"),
      description: t("Dashboard_clientcontent.features.reporting.description"),
    },
  ];

  return (
    <Box
      sx={{
        mb: 4,
        backgroundColor: "white",
        borderRadius: 2,
        boxShadow: 1,
        p: { xs: 2, md: 4 },
        maxWidth: 1200,
        mx: "auto",
        position: "relative",
      }}
    >
      {isDevelopment && <DevelopmentBadge />}
      {/* Hero Section */}
      <Grid
        container
        spacing={6}
        alignItems="center"
        justifyContent="space-between"
        sx={{
          mb: { xs: 4, md: 8 },
          flexDirection: { xs: "column-reverse", md: "row" },
        }}
      >
        {/* Bagian Teks */}
        <Grid spacing={2} columns={{ xs: 4, md: 12 }}>
          <Box
            sx={{
              px: { xs: 0, md: 4 },
              textAlign: { xs: "center", md: "left" },
              mt: { xs: 3, md: 0 }, // Add top margin on mobile
            }}
          >
            <Box
              sx={{
                textAlign: { xs: "center", md: "left" },
                mb: 3,
                mx: { xs: "auto", md: 0 }, // Center horizontally on mobile
              }}
            >
              <Box
                component="img"
                src="/assets/icon/ClientConnect.png"
                alt="Connect Logo"
                sx={{
                  width: { xs: "140px", sm: "160px", md: "220px" },
                  height: "auto",
                  maxWidth: "100%",
                }}
              />
            </Box>

            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 600, // Increased weight for better readability
                color: theme.palette.text.primary,
                mb: 2,
                fontSize: { xs: "1.5rem", md: "1.75rem" }, // Responsive font size
              }}
            >
              {t("Dashboard_clientcontent.hero.title")}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                color: theme.palette.text.secondary,
                fontSize: { xs: "0.95rem", md: "1.1rem" },
                mb: { xs: 3, md: 4 },
                lineHeight: 1.6, // Better readability
              }}
            >
              {t("Dashboard_clientcontent.hero.description")}
            </Typography>
            {!isDevelopment && (
              <Button
                variant="contained"
                onClick={handleLogin}
                size="medium"
                endIcon={<ArrowForward />}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  textTransform: "none",
                  boxShadow: theme.shadows[2],
                  transition: "all 0.3s ease",
                  minWidth: { xs: "100%", sm: "auto" }, // Full width on small mobile
                  "&:hover": {
                    boxShadow: theme.shadows[4],
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {t("Dashboard_clientcontent.buttons.goToClientConnect")}
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Features Section */}

      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 4, md: 6 }, // Added vertical padding
        }}
      >
        <Typography
          variant="h4"
          component="h2"
          align="center"
          sx={{
            fontWeight: 700, // Increased weight for better visibility
            mb: { xs: 4, md: 6 }, // Responsive margin
            color: theme.palette.primary.main,
            fontSize: { xs: "1.75rem", md: "2rem" }, // Responsive font size
          }}
        >
          {t("Dashboard_clientcontent.sections.features")}
        </Typography>

        <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
          {features.map((feature, index) => (
            <Grid
              spacing={2}
              columns={{ xs: 12, md: 4, sm: 6 }}
              key={index}
              sx={{
                display: "flex",
                justifyContent: index % 2 === 0 ? "flex-end" : "flex-start",
                [theme.breakpoints.down("sm")]: {
                  justifyContent: "center",
                },
              }}
            >
              <Card
                sx={{
                  width: "100%",
                  maxWidth: 400,
                  height: "100%",
                  minHeight: { xs: 220, md: 250 },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  p: { xs: 2, md: 3 },
                  border: "1px solid",
                  borderColor: theme.palette.grey[200],
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: 3,
                    borderColor: theme.palette.primary.light,
                    transform: "translateY(-5px)",
                  },
                }}
              >
                <Avatar
                  sx={{
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.contrastText,
                    width: 60,
                    height: 60,
                    mb: 3,
                  }}
                >
                  {feature.icon}
                </Avatar>

                <CardContent sx={{ p: 0 }}>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    {feature.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      px: { xs: 0, sm: 1, md: 2 },
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          mt: { xs: 6, md: 8 },
          p: { xs: 3, md: 6 },
          borderRadius: 3,
          textAlign: "center",
          color: "common.white",
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)",
          boxShadow: "0 8px 24px rgba(33, 150, 243, 0.3)",
          mx: { xs: 0, md: "auto" },
          maxWidth: { md: 1000 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: "50%",
            bgcolor: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -50,
            left: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            bgcolor: "rgba(255, 255, 255, 0.1)",
          }}
        />

        <Box position="relative" zIndex={1}>
          <Typography
            variant="h3"
            component="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "1.5rem", md: "1.75rem" },
              lineHeight: 1.2,
            }}
          >
            {t("Dashboard_clientcontent.cta.title")}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: { xs: 3, md: 4 },
              maxWidth: 600,
              mx: "auto",
              fontSize: { xs: "1rem", md: "1.1rem" },
              opacity: 0.9,
              lineHeight: 1.7,
            }}
          >
            {t("Dashboard_clientcontent.cta.description")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {!isDevelopment && (
              <Button
                variant="contained"
                onClick={handleLogin}
                size="medium"
                endIcon={<ArrowForward />}
                sx={{
                  px: { xs: 3, md: 4 },
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: "1rem",
                  bgcolor: "common.white",
                  color: "primary.main",
                  minWidth: { xs: "100%", sm: "auto" },
                  "&:hover": {
                    bgcolor: "common.white",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {t("Dashboard_clientcontent.buttons.goToClientConnect")}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ClientContent;
